import type { CSSResult, PropertyValues } from 'lit';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { Ref } from 'lit/directives/ref';
import lottie, { AnimationItem } from 'lottie-web/build/player/lottie_light';
import animationManifest from '@momentum-design/animations/dist/manifest';

import { Component } from '../../models';
import { ROLE } from '../../utils/roles';

import styles from './animation.styles';
import type { AnimationNames, LoopType } from './animation.types';
import { DEFAULTS } from './animation.constants';

/**
 * The `mdc-animation` component is a wrapper around the Lottie animation library.
 * It fetches the animation data dynamically based on the provided name and renders it.
 * This is a display only component that does not have any interactive functionality.
 * From accessibility perspective, (by default) it is a decorative image component.
 *
 * @tagname mdc-animation
 *
 * @event load - (React: onLoad) This event is dispatched when the animation is loaded
 * @event complete - (React: onComplete) This event is dispatched when all animation loops completed
 * @event error - (React: onError) This event is dispatched when animation loading failed
 */
class Animation extends Component {
  /**
   * Name of the animation (= filename)
   * @default undefined
   */
  @property({ type: String, reflect: true })
  name?: AnimationNames;

  /**
   * URL pointing to a Lottie JSON animation file.
   * When provided, it takes precedence over the `name` property.
   * @default undefined
   */
  @property({ type: String, reflect: true })
  src?: string;

  /**
   * How many times to loop the animation
   * - "true" - infinite
   * - "false" - no loop
   * - number - number of times to loop
   * @default 'true'
   */
  @property({ type: String, reflect: true })
  loop?: LoopType = DEFAULTS.LOOP;

  /**
   * Weather start the animation automatically
   * @default true
   */
  @property({ type: Boolean, reflect: true })
  autoplay?: boolean = DEFAULTS.AUTO_PLAY;

  /**
   * Aria-label attribute to be set for accessibility
   * @default null
   */
  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel: string | null = null;

  /**
   * Aria-labelledby attribute to be set for accessibility
   * @default null
   */
  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledby: string | null = null;

  /**
   * Lottie animation instance
   * @internal
   */
  private lottieInstance?: AnimationItem;

  /**
   * Cached animation data from the last successful fetch/import
   * @internal
   */
  private cachedAnimationData?: any;

  /**
   * Container for the animation
   * @internal
   */
  private containerRef: Ref<HTMLDivElement> = createRef();

  /**
   * Exposed API of the animation library (lottie)
   * @internal
   */
  get animation() {
    return this.lottieInstance;
  }

  private getLoopValue(): boolean | number {
    if (this.loop === 'true') return true;
    if (this.loop === 'false') return false;
    if (this.loop) return Number(this.loop);
    return true;
  }

  /**
   * Create new lottie instance for the loaded data
   */
  private onLoadSuccessHandler(animationData: any): void {
    this.cachedAnimationData = animationData;
    this.createLottieInstance(animationData);
    // Dispatch load event when animation ready to play
    this.dispatchEvent(new CustomEvent('load', { bubbles: true, cancelable: true, detail: { name: this.name } }));
  }

  /**
   * Create or re-create the lottie instance with the given animation data
   */
  private createLottieInstance(animationData: any): void {
    if (this.lottieInstance) {
      this.lottieInstance.removeEventListener('complete', this.onCompleteHandler);
      this.lottieInstance.destroy();
    }

    if (this.containerRef.value) {
      this.lottieInstance = lottie.loadAnimation({
        container: this.containerRef.value, // the dom element that will contain the animation
        renderer: 'svg',
        loop: this.getLoopValue(),
        autoplay: this.autoplay,
        animationData,
        name: this.name,
      });
      this.lottieInstance.addEventListener('complete', this.onCompleteHandler);
    }
  }

  /**
   * Error handler for animation loading
   */
  private onLoadFailHandler(error: Error): void {
    const errorEvent = new CustomEvent('error', {
      bubbles: true,
      cancelable: true,
      detail: { error },
    });
    this.dispatchEvent(errorEvent);
  }

  /**
   * Import animation data dynamically
   */
  private getAnimationData(): void {
    if (this.src) {
      this.fetchAnimationFromUrl(this.src);
    } else if (this.name && animationManifest[this.name]) {
      // Make sure the path is point to a folder (and its sub-folders) that contains animation data only
      // otherwise bundlers (eg. webpack) will try to process everything in this folder including the types.d.ts
      const path = animationManifest[this.name].replace(/^\.\/lottie/, '');

      import(`@momentum-design/animations/dist/lottie${path}`)
        .then((result: any) => this.onLoadSuccessHandler(result.default))
        .catch((error: Error) => this.onLoadFailHandler(error));
    } else {
      this.onLoadFailHandler(new Error(`Invalid animation name: ${this.name}`));
    }
  }

  /**
   * Fetch animation data from a URL
   */
  private fetchAnimationFromUrl(url: string): void {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch animation from URL: ${url} (${response.status})`);
        }
        return response.json();
      })
      .then((animationData: any) => this.onLoadSuccessHandler(animationData))
      .catch((error: Error) => this.onLoadFailHandler(error));
  }

  override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);

    // fetch animation data when the animation source changes
    if (changedProperties.has('name') || changedProperties.has('src')) {
      this.cachedAnimationData = undefined;
      this.getAnimationData();
    } else if ((changedProperties.has('loop') || changedProperties.has('autoplay')) && this.cachedAnimationData) {
      // re-create the lottie instance from cache for parameter changes,
      // because lottie does not have an API for changing them on the fly
      this.createLottieInstance(this.cachedAnimationData);
    }

    if (changedProperties.has('ariaLabel') || changedProperties.has('ariaLabelledby')) {
      this.role = this.ariaLabel || this.ariaLabelledby ? ROLE.IMG : null;
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    // Clean up the lottie instance when the component is removed from the DOM
    if (this.lottieInstance) {
      this.lottieInstance.removeEventListener('complete', this.onCompleteHandler);
      this.lottieInstance.destroy();
      this.lottieInstance = undefined;
    }
  }

  /**
   * Re-dispatch the complete event from the animation library
   *
   * This handler called with the animation instance instead of the component instance
   * so we need to bind it to the component instance. The arrow function just does that.
   * @internal
   */
  private onCompleteHandler = (): void => {
    const event = new CustomEvent('complete', {
      detail: { name: this.name },
      bubbles: true,
    });
    this.dispatchEvent(event);
  };

  override render() {
    return html`<div aria-hidden="true" ${ref(this.containerRef)}></div>`;
  }

  public static override styles: Array<CSSResult> = [...Component.styles, ...styles];
}

export default Animation;
